import useSWR from "swr";
import { fetcher } from "@/lib/api";
import { PageHeader, Section } from "@/components/Heading";
import { formatIndianCurrency } from "@/lib/format";
import { usePeriod } from "@/components/PeriodFilterBar";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, ResponsiveContainer, Tooltip, CartesianGrid, ReferenceLine,
} from "recharts";

const LABEL = { DAY: "Today", WEEK: "Last 7 days", MTD: "Month to date",
                QTD: "Quarter to date", YTD: "Year to date", LTM: "Last 12 months" };

export default function HrPerformance() {
  const { period } = usePeriod();
  const { data } = useSWR(`/hr/performance-matrix?period=${period}`, fetcher);
  if (!data) return null;
  const scatter = data.employees.map((e) => ({ ...e, z: e.salary / 1000 }));

  const Q = data.quadrant_summary;
  const total = Q.hp_ha + Q.hp_la + Q.lp_ha + Q.lp_la || 1;
  const stars = ((Q.hp_ha / total) * 100).toFixed(0);
  const risks = ((Q.lp_la / total) * 100).toFixed(0);
  const hiddenCount = data.hidden_gems?.length || 0;

  return (
    <div>
      <PageHeader
        eyebrow={`People · Performance · ${LABEL[period] || period}`}
        title="Performance × Attendance."
        lede="Each dot is an employee, sized by salary. Look for hidden gems in the bottom-left of pay but top-right of output."
      />

      {/* Narrative strip */}
      <div className="grid md:grid-cols-3 gap-px bg-border mb-10">
        <div className="bg-background p-5">
          <div className="label-caps text-positive mb-2">Star cohort</div>
          <div className="font-display text-xl">{stars}% high-perf, high-attend</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            {Q.hp_ha} people doing the heavy lifting. Plan succession + retention bonuses against this list.
          </p>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps text-destructive mb-2">Tail risk</div>
          <div className="font-display text-xl">{risks}% low-perf, low-attend</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            {Q.lp_la} people on the wrong side of both axes. PIP or part-ways within the quarter.
          </p>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps text-primary mb-2">Hidden gems</div>
          <div className="font-display text-xl">{hiddenCount} under-paid stars</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            High performance, bottom salary quartile. Promote, raise, or watch them leave — those are your only three options.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border mb-8">
        <div className="bg-background p-4"><div className="label-caps">High P / High A</div><div className="font-mono text-2xl mt-1 text-positive">{Q.hp_ha}</div></div>
        <div className="bg-background p-4"><div className="label-caps">High P / Low A</div><div className="font-mono text-2xl mt-1 text-warning">{Q.hp_la}</div></div>
        <div className="bg-background p-4"><div className="label-caps">Low P / High A</div><div className="font-mono text-2xl mt-1">{Q.lp_ha}</div></div>
        <div className="bg-background p-4"><div className="label-caps">Low P / Low A</div><div className="font-mono text-2xl mt-1 text-destructive">{Q.lp_la}</div></div>
      </div>

      <div className="hairline p-4" data-testid="performance-scatter">
        <ResponsiveContainer width="100%" height={460}>
          <ScatterChart>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
            <XAxis type="number" dataKey="performance_score" name="Performance" domain={[2, 5]} stroke="hsl(var(--muted-foreground))" fontSize={10} label={{ value: "Performance score →", position: "insideBottom", offset: -5, style: { fill: "hsl(var(--muted-foreground))", fontSize: 10 } }} />
            <YAxis type="number" dataKey="attendance_pct" name="Attendance" domain={[40, 100]} stroke="hsl(var(--muted-foreground))" fontSize={10} label={{ value: "Attendance % ↑", angle: -90, position: "insideLeft", style: { fill: "hsl(var(--muted-foreground))", fontSize: 10 } }} />
            <ZAxis type="number" dataKey="z" range={[40, 320]} />
            <ReferenceLine x={3.8} stroke="hsl(var(--border))" />
            <ReferenceLine y={85} stroke="hsl(var(--border))" />
            <Tooltip
              cursor={{ stroke: "hsl(var(--primary))", strokeOpacity: 0.4 }}
              contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
              content={({ payload }) => {
                if (!payload?.length) return null;
                const p = payload[0].payload;
                return (
                  <div className="hairline bg-popover p-3 text-xs">
                    <div className="font-display text-base mb-1">{p.name}</div>
                    <div className="text-muted-foreground">{p.department} · {p.employee_type}</div>
                    <div className="font-mono mt-1">Perf {p.performance_score} · Att {p.attendance_pct}% · {formatIndianCurrency(p.salary)}</div>
                  </div>
                );
              }}
            />
            <Scatter data={scatter} fill="hsl(var(--primary))" fillOpacity={0.75} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      <Section eyebrow="Hidden gems" title="Top performance, bottom pay">
        <div className="hairline">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th>Name</th>
                <th>Department</th>
                <th className="text-right">Performance</th>
                <th className="text-right">Attendance</th>
                <th className="text-right">Salary</th>
              </tr>
            </thead>
            <tbody>
              {data.hidden_gems.map((e) => (
                <tr key={e.emp_id} data-testid="hidden-gem-row">
                  <td className="font-display text-sm">{e.name}</td>
                  <td className="text-muted-foreground">{e.department}</td>
                  <td className="text-right text-positive">{e.performance_score}</td>
                  <td className="text-right">{e.attendance_pct}%</td>
                  <td className="text-right">{formatIndianCurrency(e.salary)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
